import type { RefObject } from 'react'
import { Flag } from 'lucide-react'
import { NavigationButtons } from '@/components/ui/navigation-buttons'
import { AddressBar, type BreadcrumbSegment } from '@/components/ui/address-bar'
import { Button } from '@/components/ui/button'
import { ConversationMoreMenu } from '@/components/features/conversation-more-menu'
import { testIds } from '@/lib/test-ids'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { dispatchChatToolbarAction } from '@/lib/chat-toolbar-events'
import { openChatFeedback, type ChatFeedbackCategory } from '@/lib/chat-feedback'

export type ChatConversationToolbarProps = {
  toolbarRef: RefObject<HTMLElement | null>
  threadId: string
  title: string
  containerWidth: number | null
  canGoBack: boolean
  canGoForward: boolean
  onGoBack: () => void
  onGoForward: () => void
  hasActiveConversation: boolean
  hasChatMessages?: boolean
}

/** Document-viewer-style toolbar for editor-hosted chat tabs. */
export function ChatConversationToolbar({
  toolbarRef,
  threadId,
  title,
  containerWidth,
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
  hasActiveConversation,
  hasChatMessages = false,
}: ChatConversationToolbarProps) {
  const breadcrumbSegments: BreadcrumbSegment[] = [
    {
      label: 'Chats',
      path: null,
      onClick: () => openSidebarMode('chats'),
    },
  ]

  const handleMoveToAgentPanel = () => {
    window.dispatchEvent(
      new CustomEvent('chat-surface-move', {
        detail: { threadId, target: 'agent-panel' as const },
      }),
    )
  }

  const handleFeedback = (category?: ChatFeedbackCategory) => {
    openChatFeedback({
      category: category ?? (hasChatMessages ? 'agent-issue' : 'feedback'),
      conversationId: hasActiveConversation ? threadId : null,
      threadId,
    })
  }

  return (
    <nav
      ref={toolbarRef}
      className="flex items-center justify-between px-4 py-2 border-b border-border bg-background/50 print-hide document-viewer-toolbar"
      aria-label="Chat actions"
      data-testid={testIds.chatConversationToolbar.root}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <NavigationButtons
          onGoBack={onGoBack}
          onGoForward={onGoForward}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
          backTestId={testIds.documentViewer.backButton}
          forwardTestId={testIds.documentViewer.forwardButton}
        />

        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <AddressBar segments={breadcrumbSegments} containerWidth={containerWidth} />
          <h1
            className="text-lg font-semibold text-foreground truncate min-w-0"
            data-testid={testIds.chatConversationToolbar.title}
            title={title}
          >
            {title}
          </h1>
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={() => handleFeedback()}
          title="Report issue or send feedback"
          aria-label="Report issue or send feedback"
          data-testid={testIds.chatConversationToolbar.feedbackButton}
        >
          <Flag className="h-4 w-4" />
        </Button>
        <ConversationMoreMenu
          conversationId={hasActiveConversation ? threadId : undefined}
          onExport={() => dispatchChatToolbarAction(threadId, 'export')}
          onCopyTranscript={() => dispatchChatToolbarAction(threadId, 'copy-transcript')}
          onRename={() => dispatchChatToolbarAction(threadId, 'rename')}
          onDelete={() => dispatchChatToolbarAction(threadId, 'delete')}
          onClearHistory={() => dispatchChatToolbarAction(threadId, 'clear-history')}
          onSettings={() => dispatchChatToolbarAction(threadId, 'settings')}
          onMoveToAgentPanel={handleMoveToAgentPanel}
          onFeedback={handleFeedback}
          onLearnFromSession={
            hasChatMessages
              ? () => dispatchChatToolbarAction(threadId, 'learn-from-session')
              : undefined
          }
          hasChatMessages={hasChatMessages}
          menuButtonTestId={testIds.chatConversationToolbar.menuButton}
        />
      </div>
    </nav>
  )
}
